import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField } from '@mui/material';
import useBackendApi from '../../../hooks/useBackendApi';
import { useFormik } from 'formik';
import { guestTestKitValidationSchema } from '../../../validations/common';
import { HeaderOptions, RequestOptions } from '../../../types/httpRequests';
import BorderedButton from '../../common/buttons/BorderedButton';
import PrimaryBlueButton from '../../common/buttons/PrimaryBlueButton';
import useSnackBarAlertMessages from '../../../hooks/useSnackBarAlertMessages';
import CustomDatePicker from '../../../CustomDatePicker';
import { TestKitGuestUserRegistration } from '../../../types/user';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';
import WarningToMailSampleBack from '../WarningToMailSampleBack';
import { CustomDialog } from '../../common/CustomDialog';
import TestKitsValidatedOver18Warning from '../TestKitsValidatedOver18Warning';
import DisableActivation from '../DisableActivation';

const backendURL = process.env.REACT_APP_BACKEND_URL;
const shopifyAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;

interface GuestTestKitActivationFormProps {
  setTestKitStep: (step: string) => void;
  setUserDetails: (userDetails: TestKitGuestUserRegistration) => void;
  activationCodeValue: string;
  disableTestKitActivation: boolean;
}

export default function GuestTestKitActivationForm({
  setTestKitStep,
  setUserDetails,
  activationCodeValue,
  disableTestKitActivation,
}: GuestTestKitActivationFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiFetchRequest } = useBackendApi();
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const { theme } = useThemeContext();

  const [errorMsgDescription, setErrorMsgDescription] = useState('');
  const [errorMsgTitle, setErrorMsgTitle] = useState('');
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  interface FormikValues {
    activationCode: string;
    birthDate: Date | null;
    email: string;
  }

  const handleSubmit = async (values: FormikValues) => {
    setIsSubmitting(true);
    const encodedActivationCode = encodeURIComponent(values.activationCode);
    const testKitValidateUserUrl = `${backendURL}/test_kits/${encodedActivationCode}/validate-guest-user`;

    let headers: HeaderOptions = {
      'x-shop-key': shopifyAccessToken,
    };

    const encodedEmail = encodeURIComponent(values.email);
    const apiRequestOptions: RequestOptions = {
      endpoint: `${testKitValidateUserUrl}?email=${encodedEmail}&dob=${values.birthDate}`,
      headers: headers,
    };

    try {
      const validateUserRequest = await apiFetchRequest(apiRequestOptions);

      if (validateUserRequest.status !== 200) {
        setErrorMsgDescription(validateUserRequest.body.error_description);
        setErrorMsgTitle(validateUserRequest.body.error_title);
        setIsErrorDialogOpen(true);
        return;
      }
      setUserDetails({
        email: values.email,
        birth_date: values.birthDate,
        activationCode: values.activationCode,
        ...validateUserRequest.body,
      });

      setTestKitStep('createYourAccount');
      return;
    } catch (err) {
      addAlertMessages({
        message: 'An error happened during the test kit activation',
        severityType: 'error',
      });
      console.log(err, 'err');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseCustomDialog = () => {
    setIsErrorDialogOpen(false);
    setErrorMsgDescription('');
    setErrorMsgTitle('');
  };

  const formik = useFormik<FormikValues>({
    validateOnMount: true,
    initialValues: {
      birthDate: null,
      activationCode: activationCodeValue,
      email: '',
    },
    validationSchema: guestTestKitValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleActivateTestSubmit = () => {
    formik.handleSubmit();
  };

  const renderFields = () => {
    return (
      <>
        <TextField
          label="Activation code"
          sx={{ mb: 2 }}
          {...formik.getFieldProps('activationCode')}
          placeholder="e.g. AB11-123-654-789"
          fullWidth
          required
          error={formik.touched.activationCode && Boolean(formik.errors.activationCode)}
          helperText={formik.touched.activationCode && formik.errors.activationCode}
        />
        <TextField
          label="Email"
          variant="outlined"
          required
          sx={{ mb: 2 }}
          {...formik.getFieldProps('email')}
          placeholder="email@example.com"
          fullWidth
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
        <CustomDatePicker
          onChange={(date: Date | null) => formik.setFieldValue('birthDate', date)}
          value={formik.values.birthDate}
          label="Birth Date"
          error={formik.touched.birthDate && Boolean(formik.errors.birthDate)}
          helperText={formik.touched.birthDate && formik.errors.birthDate}
        />
      </>
    );
  };

  return (
    <Container maxWidth="md" sx={{ marginBottom: '100px' }}>
      <React.Fragment>
        {renderSnackBarAlertComponent()}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.up('md')]: {
              flexDirection: 'row',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              [theme.breakpoints.up('md')]: {
                width: '50%',
              },
              paddingBottom: '22px',
            }}
          >
            {disableTestKitActivation ? (
              <DisableActivation />
            ) : (
              <>
                <Box sx={{ display: 'flex', margin: 'auto', pd: 2, flexDirection: 'column', maxWidth: 600 }}>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>
                    Activate test kit
                  </Typography>
                  <Box
                    component="img"
                    sx={{
                      height: '100%',
                      width: '100%',
                      marginRight: '29px',
                      marginBottom: 2,
                      display: { xs: 'block', md: 'none' },
                      marginTop: '15px',
                    }}
                    alt="Activate test kit"
                    src="/images/test-kit-activation-mobile.png"
                  />
                  <Typography variant="body2" sx={{ marginBottom: '14px', fontSize: '15px' }}>
                    To activate, please enter your test kit activation code, email and birth date.
                  </Typography>
                  {renderFields()}
                  <Typography variant="body2" sx={{ mt: 1 }} gutterBottom>
                    After taking the test, please remember to write the name and date of birth of the test subject on
                    the label provided before returning the kit to the lab.
                  </Typography>
                </Box>
                <WarningToMailSampleBack />
                <TestKitsValidatedOver18Warning />
                <Box
                  sx={{
                    display: 'flex',
                    margin: 'auto',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: { xs: 'center', md: 'space-between' },
                    maxWidth: { xs: '100%', sm: '600px' },
                  }}
                >
                  <PrimaryBlueButton
                    isLoading={isSubmitting}
                    onClick={handleActivateTestSubmit}
                    buttonText="Next"
                    sxProps={{
                      [theme.breakpoints.down('md')]: {
                        mb: 1,
                      },
                    }}
                    additionalProps={{
                      disabled: !formik.isValid || isSubmitting,
                    }}
                  />
                  <BorderedButton onClick={() => navigate('/')} buttonText="Cancel" />
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              width: '100%',
              margin: { xs: '34px auto' },
              display: 'none',
              [theme.breakpoints.up('md')]: {
                display: 'block',
                width: '50%',
                margin: '11px 0 0 29px',
              },
            }}
          >
            <img src="/images/test-kit-activation.png" style={{ width: '100%' }} alt="Activate test kit" />
          </Box>
          <CustomDialog
            title={errorMsgTitle}
            errorMessage={errorMsgDescription}
            isOpen={isErrorDialogOpen}
            onClose={handleCloseCustomDialog}
          />
        </Box>
      </React.Fragment>
    </Container>
  );
}
